import { useDispatch, useSelector } from 'react-redux';
import AuthSelectors from '../../../selectors/AuthSelectors';
import { useEffect } from 'react';
import { AuthService } from '../../../services/AuthService';
import {
    ADMIN_DASHBOARD_PATH,
    ADMIN_PATH,
    OUTPUT_REPORT_PATH,
    PARTICIPANT_PATH,
    PASSWORD_PATH,
} from '../../../constants/route.constants';
import { useNavigate } from 'react-router-dom';
import AuthLayout from '../AuthLayout/AuthLayout';
import SignIn from '../../../components/auth/SignIn/SignIn';
import IntegrationService from '../../../services/common/IntegrationService';
import { IAuthUser } from '../../../types';
import { AppDispatch } from '../../../reducers/store';
import { AuthActions } from '../../../actions';
import AdminService from '../../../common/services/AdminService';
import UserSlice from '../../../common/slices/UserSlice';
import LanguageSlice from '../../../common/slices/LanguageSlice';
import setLocaleFromUserInfo from '../../../helpers/setLocaleFromUserInfo';

export default function SignInPage() {
    const authToken = AuthSelectors.useGetAuthToken();
    const isLogged = AuthSelectors.useIsLogged();
    const { tokens, isLoading } = useSelector(AuthSelectors.getLogin);
    const dispatch = useDispatch<AppDispatch>();
    const isAnonymous = useSelector(AuthSelectors.isAnonymous);

    const navigate = useNavigate();
    const savedIntegrationParams = IntegrationService.getParams();
    const locale = useSelector(LanguageSlice.state.getLocale);

    useEffect(() => {
        if (authToken) {
            resolveLogin();
        }

        async function resolveLogin() {
            const userInfo = await dispatch(
                UserSlice.actions.getInfo({ authToken }),
            ).unwrap();

            if (userInfo?.data && locale) {
                setLocaleFromUserInfo(userInfo.data, locale, dispatch);
            }

            if (userInfo?.data?.forceChangePassword == true) {
                navigate(PASSWORD_PATH, {
                    replace: true,
                    relative: 'path',
                });
                return;
            }

            if (savedIntegrationParams) {
                navigate(`/integration?${savedIntegrationParams}`, {
                    replace: true,
                    relative: 'path',
                });
                return;
            }

            const userData = AuthService.decodeUserData(authToken);

            const state: any = {};
            if (userData?.preferredQuestionnaireId) {
                state.questionnaireId = userData.preferredQuestionnaireId;
            }

            if (
                AdminService.isAdminAllowed() &&
                AuthService.isUserAdmin(authToken)
            ) {
                navigate(`${ADMIN_PATH}/${ADMIN_DASHBOARD_PATH}`, {
                    replace: true,
                    relative: 'path',
                });
                return;
            }

            if (
                userInfo?.data?.availableQuestionnaires === 0 &&
                userInfo.data.availableResults > 0 &&
                !userInfo.data.anonymousToken &&
                !isAnonymous
            ) {
                navigate(OUTPUT_REPORT_PATH, {
                    replace: true,
                    relative: 'path',
                });
                return;
            }

            navigate(PARTICIPANT_PATH, {
                replace: true,
                relative: 'path',
                state,
            });
        }
    }, [authToken]);

    return (
        <AuthLayout>
            <SignIn
                tokens={tokens}
                isLogged={isLogged}
                isLoading={isLoading}
                onSubmit={submit}
            />
        </AuthLayout>
    );

    function submit(user: IAuthUser): void {
        dispatch(AuthActions.login(user));
    }
}
